import React from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import PageHero from "../../components/pagehero/PageHero";
import Icon from "../../components/icons/Icons";
import { openCalendly } from "../../lib/calendly";
import "./Contact.css";

const ContactFormURL = "https://5lh9umqz6j.execute-api.eu-west-1.amazonaws.com/prod/";

const toast = (text, ok) =>
  Toastify({
    text,
    duration: 5000,
    gravity: "top",
    position: "center",
    style: {
      background: ok ? "#0e2233" : "#d64545",
      color: "#fff",
      borderRadius: "10px",
      padding: "14px 20px",
      boxShadow: "0 12px 32px rgba(10, 24, 38, 0.25)",
      fontFamily: "'Inter', sans-serif",
      fontSize: "14px",
    },
  }).showToast();

const Contact = () => {
  return (
    <React.Fragment>
      <PageHero
        eyebrow="contact"
        title="Ready to begin your cloud evolution?"
        lede="Leave a message and we'll get back to you shortly — usually within one business day."
      />
      <section className="section-tight">
        <div className="container contact-grid">
          <div className="contact-info blueprint">
            <p className="eyebrow">reach us</p>
            <h2 className="contact-info-title">Let's talk about your infrastructure</h2>
            <ul className="contact-channels">
              <li>
                <a href="mailto:contact@tysonworks.com">
                  <Icon name="mail" size={17} />
                  contact@tysonworks.com
                </a>
              </li>
              <li>
                <a href="tel:+12018700763">
                  <Icon name="phone" size={17} />
                  +1 (201) 870-0763
                </a>
              </li>
              <li>
                <span>
                  <Icon name="pin" size={17} />
                  228 Park Ave S, New York, NY 10003
                </span>
              </li>
            </ul>
            <button className="btn btn-primary contact-call" onClick={openCalendly}>
              <Icon name="calendar" size={16} />
              Or book a call directly
            </button>
            <div className="contact-socials">
              <a
                href="https://www.linkedin.com/company/tysonworks"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <Icon name="linkedin" size={19} />
              </a>
              <a
                href="https://github.com/tysonworks"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
              >
                <Icon name="github" size={19} />
              </a>
              <a
                href="https://www.youtube.com/tysonworks"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <Icon name="youtube" size={19} />
              </a>
            </div>
          </div>

          <div className="contact-form card">
            <ContactForm />
          </div>
        </div>
      </section>
    </React.Fragment>
  );
};

const ContactForm = () => {
  return (
    <Formik
      initialValues={{ name: "", email: "", message: "" }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required("Name is required"),
        email: Yup.string().email("Email is invalid").required("Email is required"),
        message: Yup.string().required("Message is required"),
      })}
      onSubmit={async (fields, { setSubmitting, resetForm }) => {
        try {
          const response = await fetch(ContactFormURL, {
            method: "POST",
            body: JSON.stringify(fields),
          });

          if (!response.ok) throw new Error("Submission failed");
          toast("Thank you for reaching out! Our team will contact you shortly.", true);
          resetForm();
        } catch (error) {
          toast("Message failed to send. Please try again.", false);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form noValidate>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <Field
              id="name"
              name="name"
              type="text"
              placeholder="Jane Doe"
              className={errors.name && touched.name ? "is-invalid" : ""}
            />
            <ErrorMessage name="name" component="div" className="field-error" />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <Field
              id="email"
              name="email"
              type="email"
              placeholder="jane@company.com"
              className={errors.email && touched.email ? "is-invalid" : ""}
            />
            <ErrorMessage name="email" component="div" className="field-error" />
          </div>
          <div className="form-group">
            <label htmlFor="message">Message</label>
            <Field
              id="message"
              name="message"
              as="textarea"
              rows="6"
              placeholder="Tell us about your project or challenge…"
              className={errors.message && touched.message ? "is-invalid" : ""}
            />
            <ErrorMessage name="message" component="div" className="field-error" />
          </div>
          <button type="submit" className="btn btn-ink" disabled={isSubmitting}>
            {isSubmitting ? "Sending…" : "Send message"}
            <Icon name="arrowRight" size={16} />
          </button>
        </Form>
      )}
    </Formik>
  );
};

export default Contact;
